import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles-route-news.css';

export default function NewsRoute({
  entry,
  posts,
  newsPageSize,
  cleanText,
  formatDate,
  getPageWindow,
  postHeroFallbacks,
  inferImageDimensionsFromUrl,
  getResponsiveImageAttrs,
}) {
  const newsIntro =
    'Get updates on digital signage strategy, workplace communication trends, and practical implementation insights from the Creating Margin team.';

  const [currentPage, setCurrentPage] = useState(1);

  const sortedPosts = useMemo(() => {
    return [...posts].sort((left, right) => {
      const leftTime = Date.parse(left?.date || '') || 0;
      const rightTime = Date.parse(right?.date || '') || 0;
      return rightTime - leftTime;
    });
  }, [posts]);

  const totalPages = Math.max(1, Math.ceil(sortedPosts.length / newsPageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * newsPageSize;
  const pagedPosts = sortedPosts.slice(pageStart, pageStart + newsPageSize);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const getNewsSummary = (post) => {
    const summary = cleanText(post?.excerpt || '');
    if (!summary) {
      return 'Open article';
    }

    return summary.length > 170 ? `${summary.slice(0, 167).trimEnd()}...` : summary;
  };

  const getPostCardImage = (post) => {
    const mediaImage = Array.isArray(post?.media) && post.media.length > 0 ? post.media[0] : '';
    if (mediaImage) {
      return mediaImage;
    }

    const html = String(post?.html || '');
    const imageMatch = html.match(/<img[^>]+src=(['"])(.*?)\1/i);
    if (imageMatch?.[2]) {
      return imageMatch[2];
    }

    if (post?.path && postHeroFallbacks[post.path]) {
      return postHeroFallbacks[post.path];
    }

    return '/wp-content/uploads/2018/12/Creating-Margin.png';
  };

  return (
    <>
      <section className="news-index-hero">
        <p className="eyebrow">From the blog</p>
        <h1>{entry?.title || 'News'}</h1>
        <p>{newsIntro}</p>
      </section>

      <section className="news-index-list">
        <div className="section-heading">
          <h2>Latest Posts</h2>
        </div>
        <div className="post-grid post-grid--news">
          {pagedPosts.map((post) => {
            const image = getPostCardImage(post);
            const imageDimensions = inferImageDimensionsFromUrl(image);
            const responsiveImage = getResponsiveImageAttrs(image, '(max-width: 900px) 100vw, 33vw');

            return (
              <Link className="post-card post-card--news" to={post.path} key={post.path}>
                <div className="post-card__media">
                  <img
                    src={image}
                    alt={post.title}
                    loading="lazy"
                    width={imageDimensions?.width}
                    height={imageDimensions?.height}
                    srcSet={responsiveImage.srcSet}
                    sizes={responsiveImage.sizes}
                  />
                </div>
                <p>{formatDate(post.date)}</p>
                <h3>{post.title}</h3>
                <span>{getNewsSummary(post)}</span>
              </Link>
            );
          })}
        </div>
        {totalPages > 1 ? (
          <div className="portfolio-pagination news-pagination" aria-label="News pagination">
            <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safePage === 1}>
              Previous
            </button>
            {getPageWindow(totalPages, safePage).map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={pageNumber === safePage ? 'is-active' : ''}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={safePage === totalPages}
            >
              Next
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}
